import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';

/**
 * Protected Route Component
 * - Checks if user is authenticated
 * - Checks if user has required role
 * - Redirects to appropriate page if not authorized
 */

// Parse JWT token to get user data
export function parseToken(token) {
  if (!token) return null;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error parsing token:', e);
    return null;
  }
}

// Check if token is expired
export function isTokenExpired(token) {
  const decoded = parseToken(token);
  if (!decoded || !decoded.exp) return true;
  return decoded.exp * 1000 < Date.now();
}

// Get user role from token
export function getUserRole(token) {
  const decoded = parseToken(token);
  return decoded?.role || 'user';
}

// Get user data from token
export function getUserData(token) {
  const decoded = parseToken(token);
  if (!decoded) return null;
  
  return {
    id: decoded.user_id,
    email: decoded.email,
    role: decoded.role || 'user',
    subscription_type: decoded.subscription_type || 'trial',
    enterprise_id: decoded.enterprise_id,
    is_2fa_verified: decoded.is_2fa_verified
  };
}

// Check if user has required role
export function hasRole(token, allowedRoles) {
  const role = getUserRole(token);
  return allowedRoles.includes(role);
}

// Check if user is admin
export function isAdmin(token) {
  return hasRole(token, ['admin', 'super_admin']);
}

// Check if user is super admin
export function isSuperAdmin(token) {
  return hasRole(token, ['super_admin']);
}

/**
 * ProtectedRoute Component
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to render
 * @param {string[]} props.allowedRoles - Roles allowed to access this route
 * @param {string} props.redirectTo - Redirect path if not authorized
 */
export default function ProtectedRoute({ 
  children, 
  allowedRoles = null,  // null = any authenticated user
  redirectTo = '/login',
  requireAuth = true
}) {
  const location = useLocation();
  const token = localStorage.getItem('token');
  
  // Check authentication
  if (requireAuth) {
    if (!token) {
      console.log('[Auth] No token, redirecting to login');
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
    
    if (isTokenExpired(token)) {
      console.log('[Auth] Token expired, redirecting to login');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return <Navigate to="/login" state={{ from: location }} replace />;
    }
  }
  
  // Check role if specified
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = getUserRole(token);
    console.log('[Auth] Checking role:', userRole, 'against allowed:', allowedRoles);
    
    if (!allowedRoles.includes(userRole)) {
      console.log(`[Auth] User role "${userRole}" not in allowed roles: ${allowedRoles}`);
      
      // Redirect based on user role
      if (userRole === 'admin' || userRole === 'super_admin') {
        return <Navigate to="/admin" replace />;
      } else {
        return <Navigate to="/projet" replace />;
      }
    }
  }
  
  return children;
}

/**
 * AdminRoute - Only admins and super_admins
 */
export function AdminRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['admin', 'super_admin']} redirectTo="/projet">
      {children}
    </ProtectedRoute>
  );
}

/**
 * SuperAdminRoute - Only super_admins
 */
export function SuperAdminRoute({ children }) {
  return (
    <ProtectedRoute allowedRoles={['super_admin']} redirectTo="/admin">
      {children}
    </ProtectedRoute>
  );
}

/**
 * UserRoute - Any authenticated user
 */
export function UserRoute({ children }) {
  return (
    <ProtectedRoute>
      {children}
    </ProtectedRoute>
  );
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  const token = localStorage.getItem('token');
  const [user, setUser] = React.useState(null);
  
  React.useEffect(() => {
    if (token && !isTokenExpired(token)) {
      setUser(getUserData(token));
    } else {
      setUser(null);
    }
  }, [token]);
  
  return user;
}

/**
 * Hook to check if user has role
 */
export function useHasRole(roles) {
  const token = localStorage.getItem('token');
  return hasRole(token, roles);
}

/**
 * Hook to check if user is admin
 */
export function useIsAdmin() {
  const token = localStorage.getItem('token');
  return isAdmin(token);
}
